import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMode } from '../../context/FeaturesContext';
import { biometricsApi, sessionsApi, hubApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Activity, Dumbbell, TrendingUp, TrendingDown,
  Plus, ChevronRight, Scale, Flame, Target, BarChart3,
  Zap, Trophy, Users, Heart, AlertTriangle } from 'lucide-react';
import { EmptyState } from '../../components/ui/empty-state';
import { SkeletonPage } from '../../components/ui/skeletons';
import StreakCard from '../../components/streak/StreakCard';

// Nombres en español de las métricas y estados que devuelve el resumen biométrico.
const METRIC_LABELS = {
  weight_kg: 'Peso', body_fat_pct: 'Grasa corporal', muscle_mass_kg: 'Masa muscular',
  muscle_mass_pct: 'Masa muscular', water_pct: 'Agua corporal', visceral_fat: 'Grasa visceral',
  bone_mass_kg: 'Masa ósea', bmr_kcal: 'Metabolismo basal', metabolic_age: 'Edad metabólica',
  bmi: 'IMC', waist_cm: 'Cintura', hip_cm: 'Cadera', chest_cm: 'Pecho', arm_cm: 'Brazo',
  thigh_cm: 'Muslo', neck_cm: 'Cuello', protein_pct: 'Proteína corporal',
};
const metricLabel = (key) => METRIC_LABELS[key] || key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const STATUS_LABELS = {
  normal: 'Normal', ligeramente_alto: 'Ligeramente alto', alto: 'Alto', muy_alto: 'Muy alto',
  bajo: 'Bajo', ligeramente_bajo: 'Ligeramente bajo', elevado: 'Elevado',
};
const statusLabel = (status) => STATUS_LABELS[status] || (status || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
import MacroplanCard from '../../components/macroplan/MacroplanCard';
import FamilyDashboardCard from '../../components/family/FamilyDashboardCard';
// Fase 2 UI Pro Max: bloque hero + números animados.
import DailyHero from '../../components/dashboard/DailyHero';
import AnimatedNumber from '../../components/ui/animated-number';
import { badgeSoft, textColor } from '../../lib/colorClasses';

// Mode configuration drives the variant-specific copy and the stats cards
// shown on the dashboard. The keys map 1:1 with `useMode().variant`.
const MODE_CONFIG = {
  health: {
    label: 'Modo Salud',
    icon: Heart,
    color: 'cyan',
    headerCopy: 'Aquí está tu resumen de salud y bienestar.',
  },
  family: {
    label: 'Modo Familiar',
    icon: Users,
    color: 'emerald',
    headerCopy: 'Resumen agregado de tu familia. (Hasta 4 perfiles)',
  },
  performance: {
    label: 'Modo Rendimiento',
    icon: Zap,
    color: 'amber',
    headerCopy: 'Tus métricas de rendimiento de hoy.',
  },
  team: {
    label: 'Modo Equipo',
    icon: Trophy,
    color: 'purple',
    headerCopy: 'Estado de tu equipo. (Hasta 11 atletas)',
  },
  coach: {
    label: 'Modo Coach',
    icon: Dumbbell,
    color: 'red',
    headerCopy: 'Resumen de tus clientes.',
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, getPlanView } = useAuth();
  const { variant, loading: modeLoading } = useMode();
  // El usuario todavía no tiene plan y se le está preparando. Quien ya tiene
  // un plan activo ve su dashboard normal aunque haya uno nuevo en camino.
  const planInProgress = getPlanView('workout') === 'in_progress';
  const [loading, setLoading] = useState(true);
  const [biometricsSummary, setBiometricsSummary] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [assignedPlans, setAssignedPlans] = useState([]);
  const [today, setToday] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    // allSettled so a single 404/500 (user has no plan yet, no biometrics,
    // etc.) does NOT short-circuit the other two fetches and leave the
    // page in a half-initialized state that downstream code crashes on.
    const [bioRes, sessionsRes, plansRes, todayRes] = await Promise.allSettled([
      biometricsApi.getSummary(30),
      sessionsApi.getSessions(7),
      hubApi.getAssignedWorkouts(),
      axios.get(`${API}/calendar/today`),
    ]);
    // La tarjeta "Hoy" usa la MISMA fuente que el calendario (plan activo + día de hoy).
    if (todayRes.status === 'fulfilled') setToday(todayRes.value?.data || null);
    if (bioRes.status === 'fulfilled') {
      setBiometricsSummary(bioRes.value?.data || null);
    }
    if (sessionsRes.status === 'fulfilled') {
      setRecentSessions(sessionsRes.value?.data?.sessions || []);
    }
    if (plansRes.status === 'fulfilled') {
      setAssignedPlans(plansRes.value?.data?.plans || []);
    }
    if (bioRes.status === 'rejected') console.warn('Dashboard biometrics fetch failed:', bioRes.reason);
    if (sessionsRes.status === 'rejected') console.warn('Dashboard sessions fetch failed:', sessionsRes.reason);
    if (plansRes.status === 'rejected') console.warn('Dashboard plans fetch failed:', plansRes.reason);
    setLoading(false);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  // Only spin briefly while data is in-flight. Hard cap at 800ms of
  // visible spinner; after that we render the page even with partial
  // data so el usuario nunca queda atrapado en el toast de carga,
  // sobre todo cuando el plan aún se está generando.
  const [forceRender, setForceRender] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setForceRender(true), 800);
    return () => clearTimeout(t);
  }, []);

  if ((loading || modeLoading) && !forceRender) {
    return <SkeletonPage />;
  }

  const latestMetrics = biometricsSummary?.latest?.metrics || {};
  const standardBuckets = biometricsSummary?.latest?.derived?.standard_buckets || {};
  const completedSessions = recentSessions.filter((s) => s.status === 'completed');
  const totalVolumeKg = completedSessions.reduce(
    (acc, s) => acc + (s.live_stats?.total_volume_kg || s.summary?.total_volume_kg || 0),
    0,
  );
  const avgRpe = (() => {
    const rpes = completedSessions
      .map((s) => s.live_stats?.avg_rpe || s.summary?.avg_rpe)
      .filter((v) => typeof v === 'number');
    if (!rpes.length) return null;
    return rpes.reduce((a, b) => a + b, 0) / rpes.length;
  })();

  // Resolve the variant config; default to 'health' if backend mode is null
  const modeKey = variant && MODE_CONFIG[variant] ? variant : 'health';
  const modeCfg = MODE_CONFIG[modeKey];
  const ModeIcon = modeCfg.icon;

  // ---------------------------------------------------------------
  // Quick-stat cards adapted per mode. Each variant emphasizes
  // the metrics the user actually cares about for their objective.
  // ---------------------------------------------------------------
  const healthStats = [
    {
      icon: Scale, color: 'cyan', label: 'Peso',
      value: latestMetrics.weight_kg?.toFixed(1) || '--', unit: 'kg',
      trend: standardBuckets.bmi === 'normal' ? 'up' : 'down',
    },
    {
      icon: Flame, color: 'amber', label: 'Grasa corporal',
      value: latestMetrics.body_fat_pct?.toFixed(1) || '--', unit: '%',
    },
    {
      icon: Dumbbell, color: 'emerald', label: 'Músculo',
      value: latestMetrics.muscle_mass_kg?.toFixed(1) || '--', unit: 'kg',
    },
    {
      icon: Target, color: 'purple', label: 'Sesiones esta semana',
      value: completedSessions.length, unit: '/7',
    },
  ];

  const performanceStats = [
    {
      icon: BarChart3, color: 'cyan', label: 'Volumen semanal',
      value: totalVolumeKg ? Math.round(totalVolumeKg).toLocaleString('es-MX') : '--',
      unit: totalVolumeKg ? 'kg' : '',
    },
    {
      icon: Zap, color: 'amber', label: 'RPE promedio',
      value: avgRpe ? avgRpe.toFixed(1) : '--', unit: avgRpe ? '/10' : '',
    },
    {
      icon: Target, color: 'emerald', label: 'Sesiones esta semana',
      value: completedSessions.length, unit: '/7',
    },
    {
      icon: Scale, color: 'purple', label: 'Peso',
      value: latestMetrics.weight_kg?.toFixed(1) || '--', unit: 'kg',
    },
  ];

  const familyStats = [
    { icon: Users, color: 'emerald', label: 'Perfiles activos', value: 1, unit: '/4' },
    { icon: Target, color: 'cyan', label: 'Sesiones (todos)', value: completedSessions.length, unit: '' },
    { icon: Scale, color: 'amber', label: 'Tu peso', value: latestMetrics.weight_kg?.toFixed(1) || '--', unit: 'kg' },
    { icon: Heart, color: 'red', label: 'Plan activo', value: '✓', unit: '' },
  ];

  const teamStats = [
    { icon: Users, color: 'purple', label: 'Atletas activos', value: 1, unit: '/11' },
    { icon: BarChart3, color: 'cyan', label: 'Volumen equipo', value: totalVolumeKg ? Math.round(totalVolumeKg).toLocaleString('es-MX') : '--', unit: 'kg' },
    { icon: Zap, color: 'amber', label: 'RPE promedio', value: avgRpe ? avgRpe.toFixed(1) : '--', unit: '/10' },
    { icon: Trophy, color: 'emerald', label: 'Sesiones equipo', value: completedSessions.length, unit: '' },
  ];

  const STATS_BY_VARIANT = {
    health: healthStats,
    family: familyStats,
    performance: performanceStats,
    team: teamStats,
    coach: healthStats, // coaches shouldn't usually land here, but be safe
  };
  const stats = STATS_BY_VARIANT[modeKey];

  return (
    <div className="space-y-6 md:space-y-8 pb-20 md:pb-0" data-testid="user-dashboard">
      {/* Fase 2: DailyHero — bloque hero personal con CTA gigante.
          Reemplaza el header suave y absorbe el aviso de plan-in-progress. */}
      <DailyHero userName={user?.name} planInProgress={planInProgress} />

      {/* Sub-header con contexto de modo + accesos rápidos */}
      <div className="flex flex-wrap items-center justify-between gap-3 -mt-2">
        <span
          className={`inline-flex items-center gap-2 px-2 py-1 rounded-sm text-xs font-display font-bold uppercase tracking-[.14em] border border-bronze text-bronze bg-zinc-950 relief`}
          data-testid="mode-badge"
        >
          <ModeIcon className="w-3 h-3" />
          [{modeCfg.label}]
        </span>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/progress/weekly">
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="weekly-report-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Reporte Semanal
            </Button>
          </Link>
          <Link to="/app/progress/monthly">
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="monthly-report-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Reporte Mensual
            </Button>
          </Link>
          <Link to="/app/biometrics/new">
            <Button size="sm" className="btn-secondary" data-testid="new-measurement-btn">
              <Scale className="w-4 h-4 mr-2" />
              Nueva Medición
            </Button>
          </Link>
        </div>
      </div>

      {/* Macroplan — oculto mientras el plan está en generación/revisión
          para evitar que el componente intente leer datos que aún no existen
          y deje al usuario sin contenido visible. */}
      {!planInProgress && <MacroplanCard />}

      {/* Family card — only for family mode users con plan activo */}
      {modeKey === 'family' && !planInProgress && <FamilyDashboardCard />}

      {/* Quick Stats — Fase 2 UI Pro Max: KPI atlético con animación de conteo.
          Números con font-display + tabular-nums, hover con glow orange. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 stagger-children" data-testid="quick-stats">
        {stats.map((stat) => {
          const numericValue =
            typeof stat.value === 'number'
              ? stat.value
              : parseFloat(String(stat.value).replace(/[^\d.\-]/g, ''));
          const isNumeric = Number.isFinite(numericValue);
          return (
            <div key={stat.label} className="kpi-card group">
              <div className="flex items-center justify-between mb-2">
                <stat.icon className={`w-5 h-5 ${textColor(stat.color)} transition-transform duration-normal ease-out-atl group-hover:scale-110`} />
                {stat.trend === 'up' && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                {stat.trend === 'down' && <TrendingDown className="w-4 h-4 text-amber-400" />}
              </div>
              <p className="kpi-card__value text-3xl md:text-4xl text-white">
                [{isNumeric ? (
                  <AnimatedNumber value={numericValue} duration={900} />
                ) : (
                  stat.value
                )}
                {stat.unit && (
                  <span className="text-base md:text-lg text-zinc-400 ml-1 font-display font-semibold uppercase tracking-wider">
                    {stat.unit}
                  </span>
                )}]
              </p>
              <p className="text-zinc-500 text-xs font-body uppercase tracking-wider mt-1">
                {stat.label}
              </p>
            </div>
          );
        })}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Streak Card - Prominent placement */}
        <div className="lg:col-span-3 min-w-0">
          <StreakCard />
        </div>

        {/* Biometrics Summary */}
        <Card className="card-default lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-display text-xl text-white">BIOMETRÍA</CardTitle>
            <Link to="/app/biometrics" className="text-primary text-sm hover:text-primary">
              Ver todo <ChevronRight className="w-4 h-4 inline" />
            </Link>
          </CardHeader>
          <CardContent>
            {biometricsSummary?.summary ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-sm">
                  <div>
                    <p className="text-zinc-400 text-sm font-body">En estándar</p>
                    <p className="font-display text-2xl text-emerald-400">
                      {biometricsSummary.summary.in_standard} métricas
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-zinc-400 text-sm font-body">Fuera de estándar</p>
                    <p className="font-display text-2xl text-amber-400">
                      {biometricsSummary.summary.out_standard} métricas
                    </p>
                  </div>
                </div>

                {biometricsSummary.summary.flags?.length > 0 && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-sm">
                    <p className="text-red-400 text-sm font-body">
                      <AlertTriangle className="w-4 h-4 inline-block mr-1 -mt-0.5" aria-hidden="true" />Atención: {biometricsSummary.summary.flags.map(metricLabel).join(', ')}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {Object.entries(standardBuckets).slice(0, 4).map(([key, status]) => (
                    <div key={key} className="flex items-center justify-between gap-3 p-3 bg-zinc-900/50 rounded">
                      <span className="text-zinc-400 text-sm min-w-0">
                        {metricLabel(key)}
                      </span>
                      <span className={`
                        text-xs px-2 py-1 rounded whitespace-nowrap shrink-0
                        ${status === 'normal' ? 'status-normal' : 
                          status === 'ligeramente_alto' ? 'status-warning' : 'status-danger'}
                      `}>
                        {statusLabel(status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState
                icon={Activity}
                title="Sin mediciones aún"
                description="Registra tu primera biometría para ver tu resumen de salud y progreso."
                action={(
                  <Link to="/app/biometrics/new">
                    <Button className="btn-primary" data-testid="first-measurement-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar Primera Medición
                    </Button>
                  </Link>
                )}
              />
            )}
          </CardContent>
        </Card>

        {/* Workout Plan */}
        <Card className="card-default">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-display text-xl text-white">HOY</CardTitle>
            <Link to="/app/hub" className="text-primary text-sm hover:text-primary">
              Hub <ChevronRight className="w-4 h-4 inline" />
            </Link>
          </CardHeader>
          <CardContent>
            {assignedPlans.length > 0 && assignedPlans[0].workout_template ? (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-primary/10 to-blue-500/10 border border-primary/20 rounded-sm">
                  <p className="text-primary text-xs uppercase tracking-wider mb-1">Entrenamiento asignado</p>
                  <p className="font-display text-xl text-white">
                    {assignedPlans[0].workout_template.title}
                  </p>
                  <p className="text-zinc-400 text-sm mt-2">
                    {assignedPlans[0].workout_template.blocks?.length || 0} bloques
                  </p>
                </div>
                <Link to={`/app/hub/workout/${assignedPlans[0].workout_template.id}`}>
                  <Button className="btn-primary w-full" data-testid="start-workout-btn">
                    <Dumbbell className="w-4 h-4 mr-2" />
                    COMENZAR
                  </Button>
                </Link>
              </div>
            ) : today?.today && !today.today.is_rest_day ? (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-sm">
                  <p className="text-primary text-xs uppercase tracking-wider mb-1">
                    {today.today.status === 'completed' ? 'Completado hoy' : today.today.status === 'in_progress' ? 'Sesión en curso' : 'Tu entrenamiento de hoy'}
                  </p>
                  <p className="font-display text-xl text-white [text-wrap:balance]">{today.today.workout_day_name}</p>
                  {today.workout_details?.estimated_duration_min && (
                    <p className="text-zinc-400 text-sm mt-1">≈ {today.workout_details.estimated_duration_min} min</p>
                  )}
                </div>
                {today.today.status !== 'completed' && (
                  <Link to={`/app/workout/run/${today.today.id}`}>
                    <Button className="btn-primary w-full" data-testid="start-workout-btn">
                      <Dumbbell className="w-4 h-4 mr-2" />
                      {today.today.status === 'in_progress' ? 'Continuar' : 'Entrenar ahora'}
                    </Button>
                  </Link>
                )}
              </div>
            ) : today?.today?.is_rest_day ? (
              <EmptyState
                icon={Dumbbell}
                title="Día de descanso"
                description="Hoy toca recuperar. Tu siguiente entrenamiento está en el calendario."
                action={(
                  <Link to="/app/calendar">
                    <Button className="btn-secondary">Ver calendario</Button>
                  </Link>
                )}
              />
            ) : (
              <EmptyState
                icon={Dumbbell}
                title="Configura tu calendario"
                description="Tu plan ya está listo. Elige en qué días entrenas y tus sesiones aparecerán aquí."
                action={(
                  <Link to="/app/calendar">
                    <Button className="btn-secondary" data-testid="explore-hub-btn">
                      Ir al calendario
                    </Button>
                  </Link>
                )}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Sessions */}
      {recentSessions.length > 0 && (
        <Card className="card-default">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-display text-xl text-white">SESIONES RECIENTES</CardTitle>
            <Link to="/app/hub/history" className="text-primary text-sm hover:text-primary">
              Ver historial <ChevronRight className="w-4 h-4 inline" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentSessions.slice(0, 5).map((session) => {
                const isInProgress = session.status === 'in_progress';
                const isManual = session.manual === true;
                // I: fila accionable — en curso retoma la sesión; completada abre
                // su resumen. Las manuales (campo) no tienen resumen ejecutable.
                const to = isInProgress && session.calendar_day_id
                  ? `/app/workout/run/${session.calendar_day_id}`
                  : (!isManual && session.status === 'completed'
                      ? `/app/hub/history/${session.id}`
                      : null);
                const volume = session.live_stats?.total_volume_kg || session.summary?.total_volume_kg;

                const rowInner = (
                  <div className={`flex items-center justify-between p-4 bg-zinc-800/50 rounded-sm ${to ? 'hover:bg-zinc-800 transition-colors cursor-pointer' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                        isManual ? 'bg-purple-500/20' : session.status === 'completed' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                      }`}>
                        <Dumbbell className={`w-5 h-5 ${
                          isManual ? 'text-purple-400' : session.status === 'completed' ? 'text-emerald-400' : 'text-amber-400'
                        }`} />
                      </div>
                      <div>
                        <p className="text-white font-body text-sm">
                          {new Date(session.started_at || session.session_date).toLocaleDateString('es-MX', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-zinc-500 text-xs">
                          {isInProgress ? 'En progreso' : isManual ? 'Entrenamiento en campo' : 'Completada'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isInProgress && (
                        <span className="px-3 py-1 rounded-sm bg-primary/15 text-primary text-xs font-bold uppercase tracking-wider">
                          Retomar
                        </span>
                      )}
                      {!isInProgress && isManual && session.duration_min && (
                        <div className="text-right">
                          <p className="text-white font-display text-lg">{session.duration_min} min</p>
                          <p className="text-zinc-500 text-xs">duración</p>
                        </div>
                      )}
                      {!isInProgress && !isManual && volume ? (
                        <div className="text-right">
                          <p className="text-white font-display text-lg">
                            {(volume || 0).toLocaleString()} kg
                          </p>
                          <p className="text-zinc-500 text-xs">volumen</p>
                        </div>
                      ) : null}
                      {to && <ChevronRight className="w-4 h-4 text-zinc-500" />}
                    </div>
                  </div>
                );

                return to ? (
                  <Link key={session.id} to={to} className="block" data-testid={`recent-session-${session.id}`}>
                    {rowInner}
                  </Link>
                ) : (
                  <div key={session.id} data-testid={`recent-session-${session.id}`}>
                    {rowInner}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
